import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import useModalEscape from "../../hooks/useModalEscape";
import useModalTransition from "../../hooks/useModalTransition";
import { API_BASE_URL } from "../../config";
import { X, CreditCard, ChevronDown, Info, Check } from "lucide-react";
import "./LguRequestModal.css";

const LguRequestModal = ({
  isOpen,
  onClose,
  mode = "public", // 'public' | 'renewal'
  user = null,
}) => {
  const shouldRender = useModalTransition(isOpen);

  // Compute default requestType for renewal
  const getDefaultRequestType = () => {
    if (!user) return "";
    const lvl = String(user.adminaccessLevel || "").toLowerCase();
    if (lvl.includes("barangay")) return "Barangay";
    if (lvl.includes("muni")) return "Municipality";
    if (lvl.includes("prov")) return "Province";
    return "";
  };

  const getRenewalLocationString = () => {
    if (!user || !user.location) return "our LGU";
    const loc = user.location;
    const parts = [loc.barangay, loc.municipality, loc.province].filter(
      Boolean,
    );
    return parts.length > 0 ? parts.join(", ") : "our LGU";
  };

  const [formData, setFormData] = useState({
    email: "",
    phone: "",
    firstName: "",
    lastName: "",
    requestType: "",
    position: "",
    message: "",
    region: "",
    province: "",
    municipality: "",
    barangay: "",
  });

  const [regions, setRegions] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [municipalities, setMunicipalities] = useState([]);
  const [barangays, setBarangays] = useState([]);
  const [showPricing, setShowPricing] = useState(mode === "renewal");
  const [pricingPeriod, setPricingPeriod] = useState(
    mode === "renewal" ? "subsequent" : "initial",
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [loading, setLoading] = useState({
    regions: false,
    provinces: false,
    municipalities: false,
    barangays: false,
  });

  // Populate data when modal opens or mode changes
  useEffect(() => {
    if (isOpen) {
      if (mode === "renewal" && user) {
        const nameParts = (user.name || user.profile?.name || "")
          .trim()
          .split(" ");
        const first = nameParts[0] || "";
        const last = nameParts.slice(1).join(" ") || "";
        const reqLvl = getDefaultRequestType();
        const locStr = getRenewalLocationString();

        setFormData({
          email: user.email || "",
          phone: user.phone || "",
          firstName: first,
          lastName: last,
          requestType: reqLvl,
          position:
            user.position || (reqLvl ? `${reqLvl} Admin` : "LGU Administrator"),
          message: `Official request for Avisa LGU Partnership License Renewal for ${locStr}. We would like to extend our active subscription.`,
          region: user.location?.region || "",
          province: user.location?.province || "",
          municipality: user.location?.municipality || "",
          barangay: user.location?.barangay || "",
        });
        setShowPricing(true);
      } else {
        setFormData({
          email: "",
          phone: "",
          firstName: "",
          lastName: "",
          requestType: "",
          position: "",
          message: "",
          region: "",
          province: "",
          municipality: "",
          barangay: "",
        });
        setShowPricing(false);
      }
    }
  }, [isOpen, mode, user]);

  // Fetch regions on mount
  useEffect(() => {
    const fetchRegions = async () => {
      try {
        setLoading((prev) => ({ ...prev, regions: true }));
        const response = await fetch("https://psgc.gitlab.io/api/regions/");
        const data = await response.json();
        setRegions(data.sort((a, b) => a.name.localeCompare(b.name)));
      } catch (error) {
        console.error("Error fetching regions:", error);
      } finally {
        setLoading((prev) => ({ ...prev, regions: false }));
      }
    };
    fetchRegions();
  }, []);

  const handleRegionChange = async (e) => {
    const code = e.target.value;
    const regionName = regions.find((r) => r.code === code)?.name || "";
    setFormData((prev) => ({
      ...prev,
      region: regionName,
      province: "",
      municipality: "",
      barangay: "",
    }));
    setProvinces([]);
    setMunicipalities([]);
    setBarangays([]);

    if (!code) return;

    try {
      setLoading((prev) => ({ ...prev, provinces: true }));
      const response = await fetch(
        `https://psgc.gitlab.io/api/regions/${code}/provinces/`,
      );
      const data = await response.json();
      setProvinces(data.sort((a, b) => a.name.localeCompare(b.name)));
    } catch (error) {
      console.error("Error fetching provinces:", error);
    } finally {
      setLoading((prev) => ({ ...prev, provinces: false }));
    }
  };

  const handleProvinceChange = async (e) => {
    const code = e.target.value;
    const provName = provinces.find((p) => p.code === code)?.name || "";
    setFormData((prev) => ({
      ...prev,
      province: provName,
      municipality: "",
      barangay: "",
    }));
    setMunicipalities([]);
    setBarangays([]);

    if (!code) return;

    try {
      setLoading((prev) => ({ ...prev, municipalities: true }));
      const response = await fetch(
        `https://psgc.gitlab.io/api/provinces/${code}/cities-municipalities/`,
      );
      const data = await response.json();
      setMunicipalities(data.sort((a, b) => a.name.localeCompare(b.name)));
    } catch (error) {
      console.error("Error fetching municipalities:", error);
    } finally {
      setLoading((prev) => ({ ...prev, municipalities: false }));
    }
  };

  const handleMunicipalityChange = async (e) => {
    const code = e.target.value;
    const muniName = municipalities.find((m) => m.code === code)?.name || "";
    setFormData((prev) => ({ ...prev, municipality: muniName, barangay: "" }));
    setBarangays([]);

    if (!code) return;

    try {
      setLoading((prev) => ({ ...prev, barangays: true }));
      const response = await fetch(
        `https://psgc.gitlab.io/api/cities-municipalities/${code}/barangays/`,
      );
      const data = await response.json();
      setBarangays(data.sort((a, b) => a.name.localeCompare(b.name)));
    } catch (error) {
      console.error("Error fetching barangays:", error);
    } finally {
      setLoading((prev) => ({ ...prev, barangays: false }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    if (
      !formData.firstName ||
      !formData.lastName ||
      !formData.email ||
      !formData.requestType
    ) {
      toast.error("Please fill in all required fields.");
      return;
    }

    if (
      mode !== "renewal" &&
      (!formData.region || !formData.province || !formData.municipality)
    ) {
      toast.error(
        "Please select your location (Region, Province, Municipality).",
      );
      return;
    }

    setIsSubmitting(true);

    try {
      const docData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        name: `${formData.firstName} ${formData.lastName}`.trim(),
        email: formData.email,
        phone: formData.phone
          ? formData.phone.startsWith("+63")
            ? formData.phone
            : `+63 ${formData.phone}`
          : "",
        position: formData.position,
        level: formData.requestType,
        region: formData.region,
        province: formData.province,
        municipality: formData.municipality,
        barangay: formData.barangay,
        message: formData.message,
        requestCategory:
          mode === "renewal" ? "partnership_renewal" : "partnership_request",
      };

      const response = await fetch(
        `${API_BASE_URL}/api/public/submit-lgu-request`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(docData),
        },
      );

      let result;
      const contentType = response.headers.get("content-type");
      if (contentType && contentType.includes("application/json")) {
        result = await response.json();
      } else {
        const text = await response.text();
        throw new Error(text || "Failed to submit request");
      }

      if (!response.ok) {
        throw new Error(result.error || "Failed to submit request");
      }

      toast.success(
        mode === "renewal"
          ? "Renewal request submitted successfully! Our administrative team will process your partnership license."
          : "Your request has been submitted successfully! We will contact you soon.",
      );

      onClose();
    } catch (error) {
      console.error("Error submitting request:", error);
      toast.error(
        error.message || "Something went wrong. Please try again later.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  useModalEscape(onClose, isOpen);

  if (!shouldRender) return null;

  const isRenewal = mode === "renewal";

  return (
    <div
      className={`lgu-modal-overlay modal-transition-${isOpen ? "enter" : "exit"}`}
      onClick={onClose}
    >
      <div className="lgu-modal-container" onClick={(e) => e.stopPropagation()}>
        <button className="lgu-modal-close-btn" onClick={onClose}>
          <X size={20} />
        </button>

        <div className="lgu-modal-header">
          <h2>
            {isRenewal
              ? "Request Partnership License Renewal"
              : "Request LGU Access"}
          </h2>
          <p>
            {isRenewal
              ? "Renew your LGU's annual license to continue seamless document digitization & legal management."
              : "Partner with Avisa to digitize and manage your local laws."}
          </p>
        </div>

        {/* Pricing Accordion Toggle */}
        <div className="lgu-pricing-accordion-wrapper">
          <div className="lgu-pricing-drawer-container">
            <button
              type="button"
              className="lgu-pricing-toggle"
              onClick={() => setShowPricing(!showPricing)}
            >
              <div className="lgu-pricing-toggle-info">
                <CreditCard size={15} className="lgu-pricing-icon" />
                <span className="lgu-pricing-toggle-title">
                  View Partnership Pricing Plans
                </span>
              </div>
              <div className="lgu-pricing-toggle-right">
                <div
                  className={`lgu-pricing-chevron ${showPricing ? "rotated" : ""}`}
                >
                  <ChevronDown size={16} />
                </div>
              </div>
            </button>

            {/* CSS Grid Accordion Drawer */}
            <div
              className={`lgu-pricing-drawer ${showPricing ? "expanded open" : ""}`}
            >
              <div className="lgu-pricing-drawer-inner">
                <div className="lgu-pricing-content">
                  {/* Pricing Period / Renewal Switch */}
                  <div className="lgu-pricing-period-switch-wrapper">
                    <div className="lgu-pricing-period-tabs">
                      <button
                        type="button"
                        className={`lgu-period-tab-btn ${pricingPeriod === "initial" ? "active" : ""}`}
                        onClick={() => setPricingPeriod("initial")}
                      >
                        1st Year Onboarding
                      </button>
                      <button
                        type="button"
                        className={`lgu-period-tab-btn ${pricingPeriod === "subsequent" ? "active" : ""}`}
                        onClick={() => setPricingPeriod("subsequent")}
                      >
                        Annual Renewal & Maintenance
                      </button>
                    </div>
                  </div>

                  <div className="lgu-pricing-cards">
                    {/* Barangay Tier */}
                    <div
                      className={`lgu-price-card ${formData.requestType === "Barangay" ? "selected" : ""}`}
                      onClick={() => {
                        if (!isRenewal) {
                          setFormData({ ...formData, requestType: "Barangay" });
                        }
                      }}
                      style={
                        isRenewal && formData.requestType !== "Barangay"
                          ? { opacity: 0.6, cursor: "not-allowed" }
                          : {}
                      }
                    >
                      {formData.requestType === "Barangay" && (
                        <div className="lgu-selected-check">
                          <Check size={11} />
                        </div>
                      )}
                      <div className="lgu-price-card-header">
                        <span className="lgu-tier-label">Barangay LGU</span>
                      </div>
                      <div className="lgu-price-amount">
                        <span className="lgu-currency">
                          {pricingPeriod === "initial" ? "₱2,499" : "₱1,299"}
                        </span>
                        <span className="lgu-period">/ Year</span>
                      </div>
                      <p className="lgu-tier-desc">
                        Digital archiving &amp; local barangay records
                      </p>
                    </div>

                    {/* Municipal Tier */}
                    <div
                      className={`lgu-price-card ${formData.requestType === "Municipality" ? "selected" : ""}`}
                      onClick={() => {
                        if (!isRenewal) {
                          setFormData({
                            ...formData,
                            requestType: "Municipality",
                          });
                        }
                      }}
                      style={
                        isRenewal && formData.requestType !== "Municipality"
                          ? { opacity: 0.6, cursor: "not-allowed" }
                          : {}
                      }
                    >
                      {formData.requestType === "Municipality" && (
                        <div className="lgu-selected-check">
                          <Check size={11} />
                        </div>
                      )}
                      <div className="lgu-price-card-header">
                        <span className="lgu-tier-label">Municipal LGU</span>
                      </div>
                      <div className="lgu-price-amount">
                        <span className="lgu-currency">
                          {pricingPeriod === "initial" ? "₱12,999" : "₱6,999"}
                        </span>
                        <span className="lgu-period">/ Year</span>
                      </div>
                      <p className="lgu-tier-desc">
                        Municipal &amp; city ordinance legal management
                      </p>
                    </div>

                    {/* Provincial Tier */}
                    <div
                      className={`lgu-price-card ${formData.requestType === "Province" ? "selected" : ""}`}
                      onClick={() => {
                        if (!isRenewal) {
                          setFormData({ ...formData, requestType: "Province" });
                        }
                      }}
                      style={
                        isRenewal && formData.requestType !== "Province"
                          ? { opacity: 0.6, cursor: "not-allowed" }
                          : {}
                      }
                    >
                      {formData.requestType === "Province" && (
                        <div className="lgu-selected-check">
                          <Check size={11} />
                        </div>
                      )}
                      <div className="lgu-price-card-header">
                        <span className="lgu-tier-label">Provincial LGU</span>
                      </div>
                      <div className="lgu-price-amount">
                        <span className="lgu-currency">
                          {pricingPeriod === "initial" ? "₱24,999" : "₱13,999"}
                        </span>
                        <span className="lgu-period">/ Year</span>
                      </div>
                      <p className="lgu-tier-desc">
                        Province-wide legislative archive &amp; governance
                      </p>
                    </div>
                  </div>

                  <div className="lgu-pricing-footer-note">
                    <Info size={14} className="lgu-note-icon" />
                    <span>
                      {pricingPeriod === "initial" ? (
                        <>
                          <strong>Initial Year Subscription:</strong> Covers
                          system onboarding, digitization setup, and first-year
                          operations. Subsequent years renew at discounted
                          maintenance rates.
                        </>
                      ) : (
                        <>
                          <strong>Annual Renewal & Maintenance:</strong> Special
                          discounted renewal rate for continuous software
                          maintenance, cloud storage, security, and updates.
                        </>
                      )}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <form className="lgu-request-form" onSubmit={handleSubmit}>
          <div className="form-row">
            <div className="form-group">
              <label>First Name *</label>
              <input
                type="text"
                placeholder="Enter first name"
                value={formData.firstName}
                required
                onChange={(e) =>
                  setFormData({ ...formData, firstName: e.target.value })
                }
              />
            </div>
            <div className="form-group">
              <label>Last Name *</label>
              <input
                type="text"
                placeholder="Enter last name"
                value={formData.lastName}
                required
                onChange={(e) =>
                  setFormData({ ...formData, lastName: e.target.value })
                }
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Email *</label>
              <input
                type="email"
                placeholder="official@lgu.gov.ph"
                value={formData.email}
                required
                onChange={(e) =>
                  setFormData({ ...formData, email: e.target.value })
                }
              />
            </div>
            <div className="form-group">
              <label>Phone Number *</label>
              <input
                type="text"
                placeholder="+63 917 123 4567"
                maxLength="16"
                style={{ flex: 1 }}
                value={
                  formData.phone
                    ? formData.phone.startsWith("+63 ")
                      ? formData.phone
                      : `+63 ${formData.phone}`
                    : "+63 "
                }
                required
                onChange={(e) => {
                  let val = e.target.value;
                  if (!val.startsWith("+63 ")) {
                    val = "+63 " + val.replace(/^\+?6?3?\s?/, "");
                  }

                  const numbers = val.slice(4).replace(/\D/g, "");
                  if (numbers === "" || numbers.startsWith("9")) {
                    let formatted = "";
                    const n = numbers.slice(0, 10);
                    if (n.length > 3 && n.length <= 6) {
                      formatted = `${n.slice(0, 3)} ${n.slice(3)}`;
                    } else if (n.length > 6) {
                      formatted = `${n.slice(0, 3)} ${n.slice(3, 6)} ${n.slice(6, 10)}`;
                    } else {
                      formatted = n;
                    }
                    setFormData({ ...formData, phone: formatted });
                  }
                }}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Occupation / Designation *</label>
              <input
                type="text"
                placeholder="e.g. Mayor, Secretary, Citizen"
                value={formData.position}
                required
                onChange={(e) =>
                  setFormData({ ...formData, position: e.target.value })
                }
              />
            </div>
            <div className="form-group">
              <label>I am requesting access for *</label>
              <select
                value={formData.requestType}
                required
                disabled={isRenewal}
                onChange={(e) =>
                  setFormData({ ...formData, requestType: e.target.value })
                }
                style={
                  isRenewal
                    ? { backgroundColor: "#f1f5f9", cursor: "not-allowed" }
                    : {}
                }
              >
                <option value="">Select level...</option>
                <option value="Province">Provincial LGU</option>
                <option value="Municipality">Municipal LGU</option>
                <option value="Barangay">Barangay LGU</option>
              </select>
            </div>
          </div>

          {/* Location fields */}
          {isRenewal ? (
            <div className="form-row">
              <div className="form-group" style={{ gridColumn: "1 / -1" }}>
                <label>Registered LGU Jurisdiction</label>
                <input
                  type="text"
                  disabled
                  value={getRenewalLocationString()}
                  style={{ backgroundColor: "#f1f5f9", cursor: "not-allowed" }}
                />
              </div>
            </div>
          ) : (
            <>
              <div className="form-row">
                <div className="form-group">
                  <label>Region *</label>
                  <select required onChange={handleRegionChange}>
                    <option value="">Select Region</option>
                    {regions.map((r) => (
                      <option key={r.code} value={r.code}>
                        {r.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Province *</label>
                  <select
                    required
                    onChange={handleProvinceChange}
                    disabled={provinces.length === 0}
                  >
                    <option value="">Select Province</option>
                    {provinces.map((p) => (
                      <option key={p.code} value={p.code}>
                        {p.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Municipality / City *</label>
                  <select
                    required
                    onChange={handleMunicipalityChange}
                    disabled={municipalities.length === 0}
                  >
                    <option value="">Select Municipal</option>
                    {municipalities.map((m) => (
                      <option key={m.code} value={m.code}>
                        {m.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>
                    Barangay {formData.requestType === "Barangay" ? "*" : ""}
                  </label>
                  <select
                    required={formData.requestType === "Barangay"}
                    onChange={(e) => {
                      const code = e.target.value;
                      const bName =
                        barangays.find((b) => b.code === code)?.name || "";
                      setFormData({ ...formData, barangay: bName });
                    }}
                    disabled={barangays.length === 0}
                  >
                    <option value="">Select Barangay</option>
                    {barangays.map((b) => (
                      <option key={b.code} value={b.code}>
                        {b.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </>
          )}

          <div className="form-group">
            <label>Message</label>
            <textarea
              rows="3"
              placeholder="Any additional information or questions..."
              value={formData.message}
              onChange={(e) =>
                setFormData({ ...formData, message: e.target.value })
              }
            ></textarea>
          </div>

          <div className="lgu-modal-actions">
            <button
              type="button"
              className="btn-cancel"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button type="submit" className="btn-save" disabled={isSubmitting}>
              {isSubmitting
                ? "Submitting..."
                : isRenewal
                  ? "Submit Renewal Request"
                  : "Submit Request"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LguRequestModal;
